import React from 'react'
import { Popover } from 'antd'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import type { ColumnType } from 'antd/es/table'
import shortenText from '../../features/arendeFordelning/components/tableUtils'
import TruncatedText from '../truncatedText/TruncatedText'
import { PermissionTypes } from '../menus/utils/generateSideMenuChildren'
import { getMe } from '../../store/organizationSlice'
import '../../styles/flagTooltip.css'
import '../../styles/flagLink.css'

interface UtförareBearer {
  utförare?: { utförare_id: number; utförare_namn: string }[]
}

/**
 * Utförar-cellen. Egen komponent för att den behöver veta om tittaren har utförar-vyn
 * — kolumn-fabriken nedanför är en vanlig funktion och kan inte använda hooks.
 *
 * Selektorn returnerar en boolean, så cellen renderas bara om när behörigheten faktiskt
 * ändras (vilket den inte gör under en session). Behöver listorna optimeras går det att
 * skicka in värdet som option i stället, men det kräver att alla tre tabell-konfigurationer
 * och deras anropsställen trådar igenom det.
 */
const UtförareCell = ({ names, maxLength, url }: { names: string[]; maxLength: number; url: (namn: string) => string }) => {
  const me = useSelector(getMe)
  // Utförarens NAMN är relevant kontext på en flagga och står kvar för alla. Det är
  // vidarelänken till utförarens egen sida som inte hör hemma hos den som saknar vyn —
  // annars leder kolumnen till en 403-sida (#50). Samma modell som Ansvarig handläggare,
  // där namn bara är klickbara när de ligger i tittarens scope.
  const kanÖppna = me.permissions.includes(PermissionTypes.utförare)
  const rest = names.length - 1

  if (names.length === 1) {
    return kanÖppna ? <TruncatedText text={names[0]} to={url(names[0])} /> : <TruncatedText text={names[0]} />
  }

  // Flera → klick öppnar en ljus popover med en rad per utförare (interaktivt innehåll hör
  // hemma i en panel, inte en hover-tooltip). Cellen är bara trigger.
  return (
    <Popover
      content={
        <>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Utförare</div>
          {names.map((n, i) => (
            <div key={i}>{kanÖppna ? <Link to={url(n)}>{n}</Link> : n}</div>
          ))}
        </>
      }
      overlayClassName="flag-popover"
      placement="topLeft"
      trigger="click"
    >
      <span className="flag-utforare-cell" style={{ display: 'block', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <span className={kanÖppna ? 'flag-utforare-name' : undefined}>{shortenText(names[0], maxLength)}</span>
        {rest > 0 && <span style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}>+{rest}</span>}
      </span>
    </Popover>
  )
}

export const utförareColumn = <T extends UtförareBearer>(
  data: T[],
  options?: {
    maxLength?: number
    filteredValue?: React.Key[] | null
    width?: number
    sortOrder?: 'ascend' | 'descend' | null
    filterable?: boolean
    // Arv till utförar-länken (#39): källvyns datumfönster + avvikelse-filter.
    link?: { from?: string; to?: string; deviation?: string[] }
  }
): ColumnType<T> => {
  const maxLength = options?.maxLength ?? 24
  const link = options?.link
  // Filtrering på utförare sker via kolumn-tratt (default). När filterable=false sköts den i
  // Filter-popovern istället (brukare-listan, #33) — sorteringen finns alltid kvar i kolumnhuvudet.
  const filterable = options?.filterable ?? true
  const column: ColumnType<T> = {
    title: 'Utförare',
    dataIndex: 'utförare',
    key: 'utförare',
    showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Utförare kopplad till flaggan' },
    ...(options?.width !== undefined ? { width: options.width } : {}),
    ...(options && 'sortOrder' in options ? { sortOrder: options.sortOrder } : {}),
    render: (_, a) => {
      if (!a.utförare || a.utförare.length === 0) return <>Ingen utförare</>
      const names = a.utförare.map((u) => u.utförare_namn).sort((x, y) => x.localeCompare(y))
      // Klickbar utförare med arv av datum + avvikelse (#39). flaglevel ärvs ej (risknivå är ett
      // sållningsfilter, inte arbetskontext — beslut 2026-06-23).
      const url = (namn: string) =>
        `/app/utforare/utforare=${namn}&from=${link?.from ?? 'null'}&to=${link?.to ?? 'null'}&flaglevel=null&deviation=${link?.deviation?.length ? link.deviation : 'null'}`

      return <UtförareCell names={names} maxLength={maxLength} url={url} />
    },
    sorter: (a, b) => {
      const firstName = (r: T) => r.utförare?.map((u) => u.utförare_namn).sort((x, y) => x.localeCompare(y))[0] ?? ''
      return firstName(a).localeCompare(firstName(b))
    },
  }
  if (filterable) {
    if (options && 'filteredValue' in options) column.filteredValue = options.filteredValue
    column.filters = Array.from(
      new Set(
        data
          .map((d) => d.utförare?.map((u) => u.utförare_namn))
          .flat()
          .filter((n): n is string => !!n)
      )
    )
      .map((name) => ({ value: name, text: name }))
      .sort((a, b) => a.value.localeCompare(b.value))
    column.onFilter = (value, record) => record.utförare?.some((u) => u.utförare_namn === value) ?? false
    column.filterSearch = true
  }
  return column
}
